// ────────────────────────────────────────────────────────────────────────────
// Booking detail — projection for the expand-down booking screen (spec §6,
// "Individual Booking Modal"). The row gives the order, the organisation's
// candidate crossings and the delivery deadline; the sailing rows themselves
// come from the ferry service per candidate (`SailingOptions`), and the journey
// timeline, footer and "latest at port" follow whichever sailing the dispatcher
// has picked — computed by `buildJourney`, not baked into the detail.
// ────────────────────────────────────────────────────────────────────────────

import type { FerryCredential, FerryVehicleType } from '@my/ferry-api';
import { roadMinutesFor } from '../../../utils/roadDistance';
import type { CrossingCarrier, CrossingRoute } from '../../ferry/catalogue';
import { dayFromToday, fmtDayLabel } from './format';
import type { BookingRow, CandidateRoute, RouteOption, SailingType } from './mockData';

// ── Time helpers ─────────────────────────────────────────────────────────────

export const parseHM = (s: string): number => {
  const [h, m] = s.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};
export const fmtHM = (min: number): string => {
  const x = ((Math.round(min) % 1440) + 1440) % 1440;
  return `${String(Math.floor(x / 60)).padStart(2, '0')}:${String(x % 60).padStart(2, '0')}`;
};
export const fmtDur = (min: number): string =>
  `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, '0')}m`;
/** Fallback only — a routed truck time replaces it wherever one is available. */
const driveMinFor = roadMinutesFor;

/** Humanised overshoot, e.g. 150 → "2h 30m", 1620 → "1d 3h". */
const fmtMiss = (min: number): string => {
  const days = Math.floor(min / 1440);
  const rem = min % 1440;
  const h = Math.floor(rem / 60);
  const m = rem % 60;
  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (h) parts.push(`${h}h`);
  if (m || parts.length === 0) parts.push(`${m}m`);
  return parts.join(' ');
};

/** Minutes from today's local midnight to a UTC instant — the cockpit's clock. */
export function absMinutesFromIso(iso: string): number {
  return Math.round((new Date(iso).getTime() - dayFromToday(0).getTime()) / 60_000);
}

/** Minutes from today's local midnight to now. */
export function nowAbsMinutes(): number {
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes();
}

/** Local `YYYY-MM-DD` of a minute count from today-midnight. */
export function isoDateFromAbsMinutes(atMin: number): string {
  const d = dayFromToday(Math.floor(atMin / 1440));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Days from today to a local `YYYY-MM-DD`; null when unreadable. */
function dayOffsetOf(isoDate: string): number | null {
  const d = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return Math.round((d.getTime() - dayFromToday(0).getTime()) / 86_400_000);
}

// ── Shapes ───────────────────────────────────────────────────────────────────

/** What a sailing row needs to book through the ferry service; null = booked in the TMS only. */
export interface SailingFerryContext {
  carrierId: string;
  routeCode: string;
  originZone: string;
  destinationZone: string;
  /** The organisation's accounts at the carrier; one must be picked when there is more than one. */
  credentials: FerryCredential[];
  credentialRequired: boolean;
  credentialId: string;
  sailingUtc: string;
  arrivalUtc: string | null;
  /** The carrier's type for this load, derived as the order form derives it. */
  vehicleTypeCode: string;
  lengthMetres: string;
  vehicleType: FerryVehicleType | null;
}

export interface SailingRow {
  id: string;
  routeLabel: string; // 'Rosslare to Pembroke'
  departurePort: string; // 'Rosslare'
  arrivalPort: string; // 'Pembroke'
  roadKm: number; // arrival port → delivery
  /** Routed truck minutes for that leg; absent = the estimate is in play. */
  roadMinutes?: number;
  roadToDest: string; // '68km · 52 min to Bristol'
  preferred: boolean;
  operator: string;
  departs: string; // '20:30' in the port's clock
  departsNote?: string; // 'Latest viable'
  arrives: string; // '06:15'
  arrivesNote?: string; // 'Not viable'
  duration: string; // '3h 15m'
  /** Placeholder until a rating source exists. */
  type: SailingType;
  blockLabel: string; // 'Blk: 2' | 'Open mkt' (none held) | '' no sailing
  blockSub: string; // '2 off-pk'
  /** Indicative figures (EUR) until a rating source exists; not shown on the cockpit. */
  cost: number;
  fuel: number;
  viable: boolean;
  statusText: string;
  /** Absolute minutes from today-midnight; absent on a row with no timetable. */
  departsAtMin?: number;
  arrivesAtMin?: number;
  /** The route's check-in window before departure. */
  checkInMin: number;
  /** The organisation's pick behind the row; absent on an already-booked sailing. */
  candidate?: CandidateRoute & { entry: CrossingRoute; carrier: CrossingCarrier | null };
  ferry: SailingFerryContext | null;
}

export type StepTone = 'amber' | 'blue' | 'dark' | 'red' | 'muted';

export interface TimelineStep {
  key: string;
  label: string;
  time: string;
  sub: string;
  tone: StepTone;
  dashed?: boolean;
  /** Absolute minutes from today-midnight — drives done/upcoming on the progress bar. */
  atMin: number;
}

export interface Requirement {
  label: string;
  state: 'ok' | 'na' | 'todo';
}

/** Sailing-independent context needed to compute a journey for any selection. */
export interface BookingContext {
  customs: boolean;
  window: string;
  depPort: string;
  destination: string;
  /** Check-in window for a route whose catalogue entry carries none. */
  checkInMin: number;
  /** Base fuel surcharge (EUR) for the corridor — added to a sailing's cost. Placeholder. */
  baseFuel: number;
  plugLabel: string | null;
  hasBest: boolean;
  noFitReason?: string;
  /** Delivery deadline in absolute minutes from today-midnight (window time, or
   *  end-of-day when the window is "Any"). */
  deliveryDeadlineMin: number;
  /** Short delivery-day label, e.g. "Thu 25 Jun". */
  deliveryDateLabel: string;
}

export interface JourneyResult {
  warning: string | null;
  steps: TimelineStep[];
  latestAtPort: string;
  footer: {
    departs: string;
    arrives: string;
    type: SailingType | null;
    roadKm: number;
    driveMin: number;
    dest: string;
    total: number | null;
    plugLabel: string | null;
  };
}

/** One candidate crossing of the row: an organisation pick in one direction. */
export interface CandidateLeg {
  key: string;
  option: RouteOption;
  candidate: CandidateRoute;
}

export interface BookingDetail {
  routeLabel: string; // 'Rosslare to Bristol'
  customs: boolean;
  window: string;
  /** Placeholders until the order carries them. */
  movement: { contact: string; haulier: string };
  requirements: Requirement[];
  customsDocs: { note: string } | null;
  collection: { roadTime: string };
  candidates: CandidateLeg[];
  ctx: BookingContext;
}

// ── Detail builder ───────────────────────────────────────────────────────────

export function buildBookingDetail(row: BookingRow): BookingDetail {
  // Real departure port for the panel header / timeline: the recommended route's
  // port, else the row's resolved port. (Each sailing line below shows its own.)
  const depPort =
    row.best?.departurePort ??
    (row.portId && row.portId !== 'unassigned' ? row.portId : undefined) ??
    'Port';
  const checkInMin = row.customs ? 5 * 60 : 90; // customs 4–6h, else 90m
  const baseFuel = row.corridor.includes('EU') ? 18 : 12;
  const temperatureC = row.temperatureC ?? null;
  const hazardous = row.hazardous ?? false;

  const candidates: CandidateLeg[] = [row.best, ...row.alts].flatMap((option) =>
    option?.candidate
      ? [
          {
            key: `${option.candidate.catalogueKey}${option.candidate.reversed ? ':reverse' : ''}`,
            option,
            candidate: option.candidate,
          },
        ]
      : [],
  );

  const plugLabel = temperatureC != null ? `${temperatureC}°C declared` : null;

  return {
    routeLabel: `${depPort} to ${row.destination}`,
    customs: row.customs,
    window: row.window,
    movement: { contact: 'Driver', haulier: 'Standard' },
    requirements: [
      { label: 'Customs', state: row.customs ? 'ok' : 'na' },
      ...(temperatureC != null
        ? [{ label: `Plug-in ${temperatureC}°C`, state: 'ok' as const }]
        : []),
      { label: 'DGN', state: hazardous ? 'todo' : 'na' },
    ],
    customsDocs: row.customs ? { note: `${row.corridor} — PBN exit / GMR entry` } : null,
    collection: { roadTime: '1h 35m' },
    candidates,
    ctx: {
      customs: row.customs,
      window: row.window,
      depPort,
      destination: row.destination,
      checkInMin,
      baseFuel,
      plugLabel,
      hasBest: !!row.best,
      noFitReason: row.noFitReason,
      deliveryDeadlineMin:
        row.deliveryDayOffset * 1440 + (row.window === 'Any' ? 1439 : parseHM(row.window)),
      deliveryDateLabel: fmtDayLabel(row.deliveryDayOffset),
    },
  };
}

/** A sailing row before its times and status: what every row of one crossing shares. */
export type SailingRowBase = Omit<
  SailingRow,
  'id' | 'departs' | 'arrives' | 'duration' | 'blockLabel' | 'viable' | 'statusText' | 'ferry'
>;

const SAMPLE_DEPARTURES = ['20:30', '21:30', '17:00', '22:00'];

/**
 * The design's sample sailings for a crossing with no integration data (no
 * account at the carrier, the integration off, the ferry service down, or a
 * carrier it does not know): an early sailing the truck cannot make, the
 * crossing's own at a time by position, and a Peak alternative on the
 * recommended crossing. Times are illustrative; such a row books in the TMS
 * only, with its sample time on the booking.
 */
export function buildSampleSailings(
  base: SailingRowBase,
  key: string,
  index: number,
  crossingMin: number,
): SailingRow[] {
  const make = (args: {
    id: string;
    departs: string;
    type: SailingType;
    viable: boolean;
    departsNote?: string;
    arrivesNote?: string;
    blockLabel: string;
    blockSub: string;
    statusText: string;
    costDelta?: number;
  }): SailingRow => {
    const departsAtMin = parseHM(args.departs);
    const arrivesAtMin = departsAtMin + crossingMin;
    return {
      ...base,
      id: `${key}:${args.id}`,
      departs: args.departs,
      departsNote: args.departsNote,
      arrives: fmtHM(arrivesAtMin),
      arrivesNote: args.arrivesNote,
      duration: fmtDur(crossingMin),
      type: args.type,
      blockLabel: args.blockLabel,
      blockSub: args.blockSub,
      cost: base.cost + (args.costDelta ?? 0),
      fuel: base.fuel + (args.type === 'Peak' ? 2 : 0),
      viable: args.viable,
      statusText: args.statusText,
      departsAtMin,
      arrivesAtMin,
      ferry: null,
    };
  };

  const departs = SAMPLE_DEPARTURES[index] ?? '23:00';
  const rows: SailingRow[] = [];
  if (index === 0) {
    rows.push(
      make({
        id: 'early',
        departs: '08:30',
        type: 'Off-peak',
        viable: false,
        arrivesNote: 'Not viable',
        blockLabel: 'Blk: 4',
        blockSub: '4 off-pk',
        statusText: 'Collection cannot reach port before cutoff',
      }),
    );
  }
  rows.push(
    make({
      id: 'main',
      departs,
      type: 'Off-peak',
      viable: true,
      departsNote: 'Latest viable',
      blockLabel: index === 0 ? 'Blk: 2' : 'Open mkt',
      blockSub: index === 0 ? '2 off-pk' : '6 off-pk',
      statusText: 'On schedule',
    }),
  );
  if (index === 0) {
    rows.push(
      make({
        id: 'peak',
        departs,
        type: 'Peak',
        viable: true,
        blockLabel: 'Open mkt',
        blockSub: '0 off-pk',
        statusText: 'On schedule',
        costDelta: 50,
      }),
    );
  }
  return rows;
}

/** The road-leg text, with a "~" on an estimate so nobody plans against a guess. */
export function roadToDestText(
  roadKm: number,
  roadMinutes: number | undefined,
  destination: string,
): string {
  return roadMinutes != null
    ? `${roadKm}km · ${roadMinutes} min to ${destination}`
    : `~${roadKm}km · ~${driveMinFor(roadKm)} min to ${destination}`;
}

/**
 * The actually-booked sailing for a Booked/Confirmed row, from the TMS booking:
 * its sail date and time, and the route's typical crossing. Feeds
 * `buildJourney` + the BookingDetails summary. Null for rows with no route.
 */
export function buildBookedSailing(row: BookingRow, ctx: BookingContext): SailingRow | null {
  const r = row.best;
  const b = row.booking;
  if (!r || !b) return null;
  const depPort = r.departurePort ?? ctx.depPort;
  const dayOffset = b.sailDate ? dayOffsetOf(b.sailDate) : null;
  const departsAtMin =
    dayOffset != null && r.sailTime ? dayOffset * 1440 + parseHM(r.sailTime) : undefined;
  const crossingMin = b.typicalDurationMinutes ?? 4 * 60;
  const arrivesAtMin = departsAtMin != null ? departsAtMin + crossingMin : undefined;
  return {
    id: 'booked',
    routeLabel: `${depPort} to ${r.arrivalPort}`,
    departurePort: depPort,
    arrivalPort: r.arrivalPort,
    roadKm: r.roadKm,
    roadMinutes: r.roadMinutes,
    roadToDest: roadToDestText(r.roadKm, r.roadMinutes, ctx.destination),
    preferred: false,
    operator: r.operator,
    departs: r.sailTime ?? '—',
    arrives: arrivesAtMin != null ? fmtHM(arrivesAtMin) : '—',
    duration: fmtDur(crossingMin),
    type: r.type,
    blockLabel: '',
    blockSub: '',
    cost: r.cost,
    fuel: ctx.baseFuel + (r.type === 'Peak' ? 2 : 0),
    viable: true,
    statusText: '',
    departsAtMin,
    arrivesAtMin,
    checkInMin: b.typicalCheckInMinutes ?? ctx.checkInMin,
    ferry: null,
  };
}

// ── Journey (recomputed per selected sailing) ────────────────────────────────

export function buildJourney(sailing: SailingRow | null, ctx: BookingContext): JourneyResult {
  const emptyFooter = {
    departs: '—',
    arrives: '—',
    type: null,
    roadKm: 0,
    driveMin: 0,
    dest: ctx.destination,
    total: null,
    plugLabel: ctx.plugLabel,
  };

  if (!sailing) {
    return {
      warning: ctx.noFitReason ?? 'No viable sailing meets the delivery window',
      steps: [],
      latestAtPort: '—',
      footer: emptyFooter,
    };
  }

  // The journey timeline's own drive leg: routed when the recommendation
  // carried a real figure, the fallback heuristic otherwise.
  const drive = sailing.roadMinutes ?? driveMinFor(sailing.roadKm);

  // A row with no timetable (booked in the TMS only, the time set on the
  // order afterwards) has no journey to draw.
  if (sailing.departsAtMin == null) {
    return {
      warning: null,
      steps: [],
      latestAtPort: '—',
      footer: {
        ...emptyFooter,
        type: sailing.type,
        roadKm: sailing.roadKm,
        driveMin: drive,
        total: sailing.cost + sailing.fuel,
      },
    };
  }

  // Absolute clock: minutes from today-midnight, so crossings that roll past
  // midnight are compared correctly against the delivery deadline's own day.
  const departsAbs = sailing.departsAtMin;
  const arrivesAbs = sailing.arrivesAtMin ?? departsAbs + 4 * 60;
  const customsMin = ctx.customs ? 45 : 0;
  const clearAbs = arrivesAbs + customsMin;
  const destAbs = clearAbs + drive;
  const latestPortAbs = departsAbs - sailing.checkInMin;

  // Each clock time carries its own calendar date (e.g. "Tue 30 Jun") so the
  // day a sailing leaves vs the day it lands/delivers is explicit.
  const dateOf = (abs: number): string => fmtDayLabel(Math.floor(abs / 1440));

  const steps: TimelineStep[] = [
    {
      key: 'latest-port',
      label: 'Latest into port',
      time: fmtHM(latestPortAbs),
      sub: `${dateOf(latestPortAbs)} · Check-in ${fmtDur(sailing.checkInMin)}`,
      tone: 'amber',
      atMin: latestPortAbs,
    },
    { key: 'departs', label: 'Departs', time: fmtHM(departsAbs), sub: `${dateOf(departsAbs)} · ${sailing.departurePort}`, tone: 'blue', atMin: departsAbs },
    { key: 'crossing', label: 'Crossing', time: sailing.duration, sub: 'Duration', tone: 'muted', dashed: true, atMin: arrivesAbs },
    { key: 'arrives-port', label: 'Arrives port', time: fmtHM(arrivesAbs), sub: `${dateOf(arrivesAbs)} · ${sailing.arrivalPort}`, tone: 'dark', atMin: arrivesAbs },
  ];
  if (ctx.customs) {
    steps.push({
      key: 'customs',
      label: 'Customs clear',
      time: `~${fmtHM(clearAbs)}`,
      sub: `${dateOf(clearAbs)} · Est. 45 min`,
      tone: 'amber',
      dashed: true,
      atMin: clearAbs,
    });
  }
  steps.push(
    { key: 'drive', label: 'Drive to dest', time: `~${drive}m`, sub: `${sailing.roadKm}km`, tone: 'muted', dashed: true, atMin: destAbs },
    { key: 'arrives-dest', label: `Arrives ${ctx.destination}`, time: fmtHM(destAbs), sub: `${dateOf(destAbs)} · Estimated`, tone: 'red', atMin: destAbs },
    {
      key: 'window',
      label: 'Delivery window',
      time: ctx.window === 'Any' ? 'Any' : ctx.window,
      sub: `Required by · ${ctx.deliveryDateLabel}`,
      tone: 'red',
      atMin: ctx.deliveryDeadlineMin,
    },
  );

  let warning: string | null = null;
  if (!sailing.viable) {
    warning = sailing.statusText;
  } else {
    const miss = destAbs - ctx.deliveryDeadlineMin;
    if (miss > 0) warning = `Misses delivery by ${fmtMiss(miss)}`;
  }

  return {
    warning,
    steps,
    latestAtPort: fmtHM(latestPortAbs),
    footer: {
      departs: sailing.departs,
      arrives: sailing.arrives,
      type: sailing.type,
      roadKm: sailing.roadKm,
      driveMin: drive,
      dest: ctx.destination,
      total: sailing.cost + sailing.fuel,
      plugLabel: ctx.plugLabel,
    },
  };
}
